import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { School, PlayCircle, XCircle, TrendingUp, LayoutDashboard, MapPin, Rocket, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDashboardData } from '@/api/schools';
import type { DashboardData, ProductionTimelinePoint } from '@/types/school';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: string;
  iconColor: string;
  iconBg: string;
}

function StatCard({ title, value, subtitle, icon: Icon, trend, iconColor, iconBg }: StatCardProps) {
  return (
    <Card className="group transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn('rounded-lg p-2 shrink-0', iconBg)}>
            <Icon className={cn('h-4 w-4', iconColor)} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">{title}</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-2xl font-bold tracking-tight text-foreground">{value}</span>
              {trend && (
                <span className="flex items-center text-xs font-medium text-emerald-600">
                  <TrendingUp className="mr-0.5 h-3 w-3" />
                  {trend}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type TimelineRange = '12m' | '6m' | '3m' | '1m';

const RANGE_LABELS: Record<TimelineRange, string> = {
  '12m': '12 mois',
  '6m': '6 mois',
  '3m': '3 mois',
  '1m': 'Dernier mois',
};

function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1);
  return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
}

function sliceTimeline(timeline: ProductionTimelinePoint[], range: TimelineRange): ProductionTimelinePoint[] {
  const months = range === '12m' ? 12 : range === '6m' ? 6 : range === '3m' ? 3 : 1;
  return timeline.slice(-months);
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length || !label) return null;
  const [year, m] = label.split('-');
  const date = new Date(Number(year), Number(m) - 1);
  const formatted = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md">
      <p className="text-sm font-medium capitalize">{formatted}</p>
      <p className="text-sm text-muted-foreground">
        {payload[0].value} école{payload[0].value > 1 ? 's' : ''}
      </p>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timelineRange, setTimelineRange] = useState<TimelineRange>('12m');

  useEffect(() => {
    getDashboardData()
      .then(setData)
      .catch((err) => console.error('Dashboard load error:', err))
      .finally(() => setLoading(false));
  }, []);

  const chartData = useMemo(() => {
    if (!data?.productionTimeline) return [];
    return sliceTimeline(data.productionTimeline, timelineRange).map((pt) => ({
      ...pt,
      label: formatMonthLabel(pt.month),
    }));
  }, [data?.productionTimeline, timelineRange]);

  const stats: StatCardProps[] = [
    {
      title: 'Écoles en production',
      value: loading ? '...' : String(data?.stats.production ?? 0),
      subtitle: 'Écoles actives',
      icon: School,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Écoles créées',
      value: loading ? '...' : String(data?.stats.created ?? 0),
      subtitle: 'En attente de mise en production',
      icon: PlayCircle,
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
    },
    {
      title: 'Écoles en démo',
      value: loading ? '...' : String(data?.stats.demo ?? 0),
      subtitle: 'Démonstration en cours',
      icon: Rocket,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
    },
    {
      title: 'Écoles résiliées',
      value: loading ? '...' : String(data?.stats.cancelled ?? 0),
      subtitle: 'Comptes désactivés',
      icon: XCircle,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <LayoutDashboard className="h-5 w-5" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            Tableau de bord
          </h1>
        </div>
        <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
          Bienvenue, {user?.firstname} ! Voici un aperçu de votre plateforme.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      {/* Production Timeline Chart */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white">
                <TrendingUp className="h-5 w-5" />
              </div>
              <span>Mises en production</span>
            </CardTitle>
            <div className="flex gap-1">
              {(Object.keys(RANGE_LABELS) as TimelineRange[]).map((range) => (
                <button
                  key={range}
                  onClick={() => setTimelineRange(range)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    timelineRange === range
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80',
                  )}
                >
                  {RANGE_LABELS[range]}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#colorCount)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-16 text-center text-sm text-muted-foreground">Aucune donnée</p>
          )}
        </CardContent>
      </Card>

      {/* Two-column layout for tables */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Latest V2 Schools */}
        <Card>
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white">
                <Rocket className="h-5 w-5" />
              </div>
              <span>Dernières écoles V2</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : data?.latestV2Schools && data.latestV2Schools.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead className="text-right">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.latestV2Schools.map((school) => (
                    <TableRow key={school.code}>
                      <TableCell>
                        <Link
                          to={`/schools/${school.code}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {school.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                          school.status === 'production' && 'bg-emerald-100 text-emerald-700',
                          school.status === 'cancelled' && 'bg-red-100 text-red-700',
                          (school.status === 'demo_empty' || school.status === 'demo_datas') && 'bg-orange-100 text-orange-700',
                          (school.status === 'created' || school.status === 'created_datas') && 'bg-violet-100 text-violet-700',
                        )}>
                          {school.statusLabel}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {school.productName ?? '—'}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                        {school.createdAt
                          ? new Date(school.createdAt).toLocaleDateString('fr-FR')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">Aucune école V2</p>
            )}
          </CardContent>
        </Card>

        {/* Academy Counts */}
        <Card>
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white">
                <MapPin className="h-5 w-5" />
              </div>
              <span>Écoles par académie</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : data?.academyCounts && data.academyCounts.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Académie</TableHead>
                    <TableHead className="text-right">Écoles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.academyCounts.map((ac) => (
                    <TableRow key={ac.code} className={ac.count === 0 ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">
                        {ac.count > 0 ? (
                          <Link to={`/schools?academie=${ac.code}`} className="text-primary hover:underline">
                            {ac.name}
                          </Link>
                        ) : (
                          ac.name
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                          ac.count > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                        )}>
                          {ac.count}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
